import { Button, Spinner } from "@sellar-io/components";
import { useMemo, type ReactNode } from "react";
import { Navigate } from "react-router";
import { Alert } from "~/components/alert/Alert";
import { PageContainer } from "~/components/pageContainer/PageContainer";
import { BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING } from "~/constants/routes";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useRyftManagementAccount } from "~/hooks/api/v2/ryftManagement/useRyftManagementAccount";
import { RyftNonHostedDashboardAccountContext } from "./RyftNonHostedDashboardAccountProvider.shared";

export const RyftNonHostedDashboardAccountProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const query = useRyftManagementAccount();

  if (query.isPending) {
    return (
      <PageContainer className="flex min-h-96 items-center justify-center">
        <Spinner />
      </PageContainer>
    );
  }

  if (query.isError) {
    const error = apiV2AssertError(query.error);
    const message = error?.response?.data.message;
    if (
      error?.response?.status === 400 &&
      message ===
        "This supplier does not have an active API-managed Ryft payout account."
    ) {
      return (
        <Navigate
          to={BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING}
          replace
        />
      );
    }

    return (
      <PageContainer className="flex flex-col gap-4 p-4">
        <Alert
          type="error"
          message={message ?? "Unable to load your Ryft account"}
        />
        <Button color="secondary" onClick={() => query.refetch()}>
          Try again
        </Button>
      </PageContainer>
    );
  }

  const account = query.data;

  if (!account.termsOfService?.acceptance) {
    return (
      <Navigate to={BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING} replace />
    );
  }

  return <MemoizedProvider account={account}>{children}</MemoizedProvider>;
};

const MemoizedProvider = ({
  account,
  children,
}: {
  account: RyftNonHostedDashboardAccountContext["account"];
  children: ReactNode;
}) => {
  const value = useMemo(() => {
    return {
      account,
    };
  }, [account]);

  return (
    <RyftNonHostedDashboardAccountContext.Provider value={value}>
      {children}
    </RyftNonHostedDashboardAccountContext.Provider>
  );
};
